import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  useAuth,
  useCreateTenantStudioCategory,
  useCreateTenantStudioLocation,
  useCreateTenantStudioSeason,
  useDeleteTenantStudioCategory,
  useDeleteTenantStudioLocation,
  useDeleteTenantStudioSeason,
  useTenantBootstrap,
  useTenantStudioSettings,
  useUpdateTenantStudioCategory,
  useUpdateTenantStudioLocation,
  useUpdateTenantStudioSeason,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, Input } from '@tark/shared/components';
import { cn } from '@tark/shared/lib';
import CoachPageHeader from '../components/CoachPageHeader';
import type {
  TenantStudioCategory,
  TenantStudioCategoryPayload,
  TenantStudioLocation,
  TenantStudioLocationPayload,
  TenantStudioSeason,
  TenantStudioSeasonPayload,
} from '@tark/shared/types';
import {
  Archive,
  ArrowRightLeft,
  BookOpenCheck,
  CalendarDays,
  ChevronRight,
  MapPin,
  Plus,
  Settings,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MANAGE_ROLES = ['OWNER', 'ADMIN', 'COACH', 'STAFF'];

type SettingsModal =
    | { kind: 'location'; item?: TenantStudioLocation | null }
    | { kind: 'season'; item?: TenantStudioSeason | null }
    | { kind: 'category'; item?: TenantStudioCategory | null }
    | null;

type LocationDraft = {
  displayName: string;
  courtLabel: string;
  addressLine1: string;
  city: string;
  stateCode: string;
  zipcode: string;
  sortOrder: string;
  active: boolean;
};

type SeasonDraft = {
  displayName: string;
  startsMonth: string;
  startsDay: string;
  endsMonth: string;
  endsDay: string;
  sortOrder: string;
  active: boolean;
};

type CategoryDraft = {
  displayName: string;
  ageLabel: string;
  levelLabel: string;
  programType: string;
  sportCode: string;
  priceAmount: string;
  priceLabel: string;
  scheduleLabel: string;
  daysRule: string;
  capacity: string;
  sortOrder: string;
  active: boolean;
};

// ============================================
// Section accent palette
// Each section gets its own tint so the three cards
// read as distinct surfaces on a wide desktop.
// ============================================
type SectionAccent = {
  // Tailwind utility fragments kept as full class strings so JIT picks them up.
  iconBg: string;
  iconText: string;
  ringHover: string;
  topStrip: string;
  countBg: string;
  countText: string;
};

const ACCENTS: Record<'location' | 'season' | 'category', SectionAccent> = {
  location: {
    iconBg: 'bg-emerald-50',
    iconText: 'text-emerald-600',
    ringHover: 'hover:border-emerald-200',
    topStrip: 'from-emerald-400/70 via-emerald-500/40 to-transparent',
    countBg: 'bg-emerald-50',
    countText: 'text-emerald-700',
  },
  season: {
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-600',
    ringHover: 'hover:border-amber-200',
    topStrip: 'from-amber-400/70 via-amber-500/40 to-transparent',
    countBg: 'bg-amber-50',
    countText: 'text-amber-700',
  },
  category: {
    iconBg: 'bg-indigo-50',
    iconText: 'text-indigo-600',
    ringHover: 'hover:border-indigo-200',
    topStrip: 'from-indigo-400/70 via-indigo-500/40 to-transparent',
    countBg: 'bg-indigo-50',
    countText: 'text-indigo-700',
  },
};

export default function CoachStudioSettingsPage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const navigate = useNavigate();
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const canManage = MANAGE_ROLES.includes(activeTenant?.roleCode?.toUpperCase() || '');
  const settingsQuery = useTenantStudioSettings(Boolean(user && isCoachStudio));
  const settings = settingsQuery.data;

  const createLocation = useCreateTenantStudioLocation();
  const updateLocation = useUpdateTenantStudioLocation();
  const deleteLocation = useDeleteTenantStudioLocation();
  const createSeason = useCreateTenantStudioSeason();
  const updateSeason = useUpdateTenantStudioSeason();
  const deleteSeason = useDeleteTenantStudioSeason();
  const createCategory = useCreateTenantStudioCategory();
  const updateCategory = useUpdateTenantStudioCategory();
  const deleteCategory = useDeleteTenantStudioCategory();

  const [modal, setModal] = useState<SettingsModal>(null);
  const [locationDraft, setLocationDraft] = useState<LocationDraft>(() => emptyLocationDraft());
  const [seasonDraft, setSeasonDraft] = useState<SeasonDraft>(() => emptySeasonDraft());
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft>(() => emptyCategoryDraft());
  const [formError, setFormError] = useState<string | null>(null);

  const activeCounts = useMemo(() => ({
    locations: settings?.locations.filter((item) => item.active).length ?? 0,
    seasons: settings?.seasons.filter((item) => item.active).length ?? 0,
    categories: settings?.categories.filter((item) => item.active).length ?? 0,
  }), [settings]);

  const totalActive =
      activeCounts.locations + activeCounts.seasons + activeCounts.categories;

  useEffect(() => {
    setFormError(null);
    if (!modal) {
      return;
    }
    if (modal.kind === 'location') {
      setLocationDraft(toLocationDraft(modal.item));
    }
    if (modal.kind === 'season') {
      setSeasonDraft(toSeasonDraft(modal.item));
    }
    if (modal.kind === 'category') {
      setCategoryDraft(toCategoryDraft(modal.item));
    }
  }, [modal]);

  if (!isCoachStudio) {
    return (
        <div className="mx-auto max-w-3xl px-1 pb-24 md:pb-8">
          <Card variant="default">
            <CardContent className="space-y-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Settings className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
                <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">Settings need a Coach Studio</h1>
                <p className="mt-2 text-sm text-text-muted">
                  Switch into an enabled coach workspace to manage reusable seasons, locations, and class categories.
                </p>
              </div>
              <Button leftIcon={<ArrowRightLeft className="h-4 w-4" />} onClick={() => navigate('/welcome')}>
                Open Home
              </Button>
            </CardContent>
          </Card>
        </div>
    );
  }

  const isSaving =
      createLocation.isPending ||
      updateLocation.isPending ||
      deleteLocation.isPending ||
      createSeason.isPending ||
      updateSeason.isPending ||
      deleteSeason.isPending ||
      createCategory.isPending ||
      updateCategory.isPending ||
      deleteCategory.isPending;

  const closeModal = () => {
    setModal(null);
    setFormError(null);
  };

  const saveLocation = async () => {
    const payload = buildLocationPayload(locationDraft);
    if (!payload.displayName) {
      setFormError('Add a location name.');
      return;
    }
    try {
      if (modal?.kind === 'location' && modal.item) {
        await updateLocation.mutateAsync({ locationId: modal.item.locationId, payload });
      } else {
        await createLocation.mutateAsync(payload);
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save Studio location', error);
      setFormError('Could not save this location.');
    }
  };

  const saveSeason = async () => {
    const payload = buildSeasonPayload(seasonDraft);
    if (!payload.displayName) {
      setFormError('Add a season name.');
      return;
    }
    try {
      if (modal?.kind === 'season' && modal.item) {
        await updateSeason.mutateAsync({ seasonId: modal.item.seasonId, payload });
      } else {
        await createSeason.mutateAsync(payload);
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save Studio season', error);
      setFormError('Could not save this season.');
    }
  };

  const saveCategory = async () => {
    const payload = buildCategoryPayload(categoryDraft);
    if (!payload.displayName) {
      setFormError('Add a class category.');
      return;
    }
    try {
      if (modal?.kind === 'category' && modal.item) {
        await updateCategory.mutateAsync({ categoryId: modal.item.categoryId, payload });
      } else {
        await createCategory.mutateAsync(payload);
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save Studio category', error);
      setFormError('Could not save this class category.');
    }
  };

  const makeInactive = async () => {
    if (!modal?.item) {
      return;
    }
    try {
      if (modal.kind === 'location') {
        await deleteLocation.mutateAsync(modal.item.locationId);
      }
      if (modal.kind === 'season') {
        await deleteSeason.mutateAsync(modal.item.seasonId);
      }
      if (modal.kind === 'category') {
        await deleteCategory.mutateAsync(modal.item.categoryId);
      }
      closeModal();
    } catch (error) {
      console.error('Failed to deactivate Studio setting', error);
      setFormError('Could not make this inactive.');
    }
  };

  return (
      <div className="w-full space-y-6 pb-24 md:pb-10">
        <CoachPageHeader
          title="Studio Settings"
          subtitle="Manage the reusable locations, seasons, and class categories used when building classes."
          meta={
            totalActive > 0 ? (
              <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
                {totalActive} active preset{totalActive === 1 ? '' : 's'}
              </span>
            ) : null
          }
          action={
            canManage ? (
              <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModal({ kind: 'location' })}>
                Add Location
              </Button>
            ) : null
          }
          stats={
            <div className="grid gap-3 sm:grid-cols-3">
              <SettingsStat
                  label="Locations"
                  value={activeCounts.locations}
                  icon={<MapPin className="h-4 w-4" />}
                  accent={ACCENTS.location}
              />
              <SettingsStat
                  label="Seasons"
                  value={activeCounts.seasons}
                  icon={<CalendarDays className="h-4 w-4" />}
                  accent={ACCENTS.season}
              />
              <SettingsStat
                  label="Categories"
                  value={activeCounts.categories}
                  icon={<BookOpenCheck className="h-4 w-4" />}
                  accent={ACCENTS.category}
              />
            </div>
          }
        />

        {/* ========== LOADING ========== */}
        {settingsQuery.isLoading ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                  <SectionSkeleton key={i} />
              ))}
            </div>
        ) : null}

        {/* ========== ERROR ========== */}
        {settingsQuery.isError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
              Studio settings could not load right now.
            </div>
        ) : null}

        {/* ========== SECTIONS ========== */}
        {!settingsQuery.isLoading && !settingsQuery.isError ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              <SettingsSection
                  accent={ACCENTS.location}
                  icon={<MapPin className="h-5 w-5" />}
                  title="Locations"
                  description="Courts, addresses, and meeting points"
                  count={settings?.locations.length ?? 0}
                  actionLabel="Add Location"
                  disabled={!canManage}
                  onAdd={() => setModal({ kind: 'location' })}
              >
                {(settings?.locations ?? []).length ? (
                    settings?.locations.map((location) => (
                        <SettingsRow
                            key={location.locationId}
                            title={location.displayName}
                            subtitle={[location.courtLabel, location.zipcode].filter(Boolean).join(' / ') || 'Location preset'}
                            active={location.active}
                            disabled={!canManage}
                            onClick={() => setModal({ kind: 'location', item: location })}
                        />
                    ))
                ) : (
                    <EmptySetting
                        icon={<MapPin className="h-5 w-5" />}
                        accent={ACCENTS.location}
                        label="No locations yet"
                        hint="Add courts, parks, or studios so coaches can pick them when scheduling."
                        action="Add Location"
                        onClick={() => setModal({ kind: 'location' })}
                        disabled={!canManage}
                    />
                )}
              </SettingsSection>

              <SettingsSection
                  accent={ACCENTS.season}
                  icon={<CalendarDays className="h-5 w-5" />}
                  title="Seasons"
                  description="Repeating windows for programs"
                  count={settings?.seasons.length ?? 0}
                  actionLabel="Add Season"
                  disabled={!canManage}
                  onAdd={() => setModal({ kind: 'season' })}
              >
                {(settings?.seasons ?? []).length ? (
                    settings?.seasons.map((season) => (
                        <SettingsRow
                            key={season.seasonId}
                            title={season.displayName}
                            subtitle={formatSeasonWindow(season)}
                            active={season.active}
                            disabled={!canManage}
                            onClick={() => setModal({ kind: 'season', item: season })}
                        />
                    ))
                ) : (
                    <EmptySetting
                        icon={<CalendarDays className="h-5 w-5" />}
                        accent={ACCENTS.season}
                        label="No seasons yet"
                        hint="Set Summer, Fall, Winter, or any window your programs run on."
                        action="Add Season"
                        onClick={() => setModal({ kind: 'season' })}
                        disabled={!canManage}
                    />
                )}
              </SettingsSection>

              <SettingsSection
                  accent={ACCENTS.category}
                  icon={<BookOpenCheck className="h-5 w-5" />}
                  title="Class Categories"
                  description="Class blueprints with pricing"
                  count={settings?.categories.length ?? 0}
                  actionLabel="Add Category"
                  disabled={!canManage}
                  onAdd={() => setModal({ kind: 'category' })}
              >
                {(settings?.categories ?? []).length ? (
                    settings?.categories.map((category) => (
                        <SettingsRow
                            key={category.categoryId}
                            title={category.displayName}
                            subtitle={[category.ageLabel, category.levelLabel, category.priceLabel || formatMoney(category.priceAmount)].filter(Boolean).join(' / ') || 'Class preset'}
                            active={category.active}
                            disabled={!canManage}
                            onClick={() => setModal({ kind: 'category', item: category })}
                        />
                    ))
                ) : (
                    <EmptySetting
                        icon={<BookOpenCheck className="h-5 w-5" />}
                        accent={ACCENTS.category}
                        label="No class categories yet"
                        hint="Build reusable class blueprints: age, level, price, schedule."
                        action="Add Category"
                        onClick={() => setModal({ kind: 'category' })}
                        disabled={!canManage}
                    />
                )}
              </SettingsSection>
            </div>
        ) : null}

        <SettingsModalShell
            open={modal !== null}
            title={modal?.kind === 'location' ? 'Location' : modal?.kind === 'season' ? 'Season' : 'Class Category'}
            subtitle={
              modal?.kind === 'location'
                  ? 'Where classes physically meet'
                  : modal?.kind === 'season'
                      ? 'A repeating window for programs'
                      : 'A reusable class blueprint'
            }
            onClose={closeModal}
            actionSlot={
              modal?.item?.active ? (
                  <Button type="button" size="sm" variant="outline" leftIcon={<Archive className="h-4 w-4" />} isLoading={isSaving} onClick={makeInactive}>
                    Make Inactive
                  </Button>
              ) : null
            }
        >
          {modal?.kind === 'location' ? (
              <LocationForm draft={locationDraft} onChange={setLocationDraft} error={formError} isSaving={isSaving} onSave={saveLocation} onCancel={closeModal} />
          ) : null}
          {modal?.kind === 'season' ? (
              <SeasonForm draft={seasonDraft} onChange={setSeasonDraft} error={formError} isSaving={isSaving} onSave={saveSeason} onCancel={closeModal} />
          ) : null}
          {modal?.kind === 'category' ? (
              <CategoryForm draft={categoryDraft} onChange={setCategoryDraft} error={formError} isSaving={isSaving} onSave={saveCategory} onCancel={closeModal} />
          ) : null}
        </SettingsModalShell>
      </div>
  );
}

// ============================================
// Section card
// ============================================
function SettingsSection({
                           accent,
                           icon,
                           title,
                           description,
                           count,
                           actionLabel,
                           disabled,
                           onAdd,
                           children,
                         }: {
  accent: SectionAccent;
  icon: ReactNode;
  title: string;
  description?: string;
  count: number;
  actionLabel: string;
  disabled?: boolean;
  onAdd: () => void;
  children: ReactNode;
}) {
  return (
      <div
          className={cn(
              'group relative flex flex-col overflow-hidden rounded-lg border border-border bg-white',
              'shadow-[var(--shadow-card)] transition-all duration-200',
              'hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)]',
              accent.ringHover
          )}
      >
        {/* Color accent strip at the very top */}
        <div
            aria-hidden
            className={cn('h-1 w-full bg-gradient-to-r', accent.topStrip)}
        />

        <div className="flex flex-col gap-4 p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div
                  className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
                      accent.iconBg,
                      accent.iconText
                  )}
              >
                {icon}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-text md:text-lg">{title}</h2>
                  <span
                      className={cn(
                          'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                          accent.countBg,
                          accent.countText
                      )}
                  >
                  {count}
                </span>
                </div>
                {description ? (
                    <p className="mt-0.5 text-xs font-medium text-text-muted md:text-[13px]">{description}</p>
                ) : null}
              </div>
            </div>
            <Button
                type="button"
                size="sm"
                variant="outline"
                leftIcon={<Plus className="h-4 w-4" />}
                disabled={disabled}
                onClick={onAdd}
            >
              <span className="hidden sm:inline">{actionLabel}</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>

          <div className="space-y-2">{children}</div>
        </div>
      </div>
  );
}

// ============================================
// Settings row
// ============================================
function SettingsRow({
                       title,
                       subtitle,
                       active,
                       disabled,
                       onClick,
                     }: {
  title: string;
  subtitle: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
      <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={cn(
              'group/row flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3.5 py-3 text-left',
              'transition-all duration-150',
              'hover:border-primary/40 hover:bg-white hover:shadow-sm',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              'disabled:cursor-default disabled:hover:border-border disabled:hover:bg-surface disabled:hover:shadow-none'
          )}
      >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-text">{title}</span>
        <span className="mt-0.5 block truncate text-xs font-medium text-text-muted">{subtitle}</span>
      </span>
        <span className="flex shrink-0 items-center gap-2">
        <StatusPill active={active} />
          {!disabled ? (
              <ChevronRight className="h-4 w-4 text-text-subtle transition-transform group-hover/row:translate-x-0.5 group-hover/row:text-primary" />
          ) : null}
      </span>
      </button>
  );
}

// ============================================
// Empty state
// ============================================
function EmptySetting({
                        icon,
                        accent,
                        label,
                        hint,
                        action,
                        disabled,
                        onClick,
                      }: {
  icon?: ReactNode;
  accent?: SectionAccent;
  label: string;
  hint?: string;
  action: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-surface-elevated/40 px-4 py-6 text-center">
        {icon && accent ? (
            <div
                className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg',
                    accent.iconBg,
                    accent.iconText
                )}
            >
              {icon}
            </div>
        ) : null}
        <div>
          <p className="text-sm font-semibold text-text">{label}</p>
          {hint ? <p className="mt-1 text-xs text-text-muted">{hint}</p> : null}
        </div>
        <Button
            size="sm"
            variant="outline"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={disabled}
            onClick={onClick}
        >
          {action}
        </Button>
      </div>
  );
}

// ============================================
// Stat tile (used in hero)
// ============================================
function SettingsStat({
                        label,
                        value,
                        icon,
                        accent,
                      }: {
  label: string;
  value: number;
  icon?: ReactNode;
  accent?: SectionAccent;
}) {
  return (
      <div className="relative overflow-hidden rounded-lg border border-border bg-white/80 px-3 py-3 backdrop-blur sm:px-4 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle sm:text-[11px]">
            {label}
          </p>
          {icon && accent ? (
              <span
                  className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                      accent.iconBg,
                      accent.iconText
                  )}
              >
            {icon}
          </span>
          ) : null}
        </div>
        <p className="mt-2 text-2xl font-bold leading-none tracking-tight text-text sm:text-3xl">{value}</p>
      </div>
  );
}

// ============================================
// Skeleton (loading state)
// ============================================
function SectionSkeleton() {
  return (
      <div className="overflow-hidden rounded-lg border border-border bg-white shadow-[var(--shadow-card)]">
        <div className="h-1 w-full bg-slate-100" />
        <div className="space-y-4 p-5 md:p-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-slate-100" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-24 rounded bg-slate-100" />
              <div className="h-3 w-36 rounded bg-slate-100/70" />
            </div>
          </div>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 rounded-lg border border-border bg-surface" />
            ))}
          </div>
        </div>
      </div>
  );
}

// ============================================
// Status pill
// ============================================
function StatusPill({ active }: { active: boolean }) {
  return (
      <span
          className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em]',
              active
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-slate-100 text-slate-600'
          )}
      >
      <span
          className={cn(
              'h-1.5 w-1.5 rounded-full',
              active ? 'bg-emerald-500' : 'bg-slate-400'
          )}
      />
        {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ============================================
// Modal shell
// ============================================
function SettingsModalShell({
                              open,
                              title,
                              subtitle,
                              actionSlot,
                              onClose,
                              children,
                            }: {
  open: boolean;
  title: string;
  subtitle?: string;
  actionSlot?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
      <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-6 sm:py-8"
          onClick={(event) => {
            // Click on the backdrop closes the modal
            if (event.target === event.currentTarget) {
              onClose();
            }
          }}
      >
        <div className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-white shadow-2xl">
          <div className="relative flex items-start justify-between gap-3 border-b border-border bg-gradient-to-br from-primary/5 via-white to-white px-5 py-4 md:px-6">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight text-text md:text-xl">{title}</h2>
              {subtitle ? (
                  <p className="mt-0.5 text-xs font-medium text-text-muted md:text-sm">{subtitle}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {actionSlot}
              <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-text-muted transition-colors hover:bg-surface-elevated hover:text-text"
                  aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto p-5 md:p-6">{children}</div>
        </div>
      </div>
  );
}

// ============================================
// Forms
// ============================================
function LocationForm({
                        draft,
                        error,
                        isSaving,
                        onChange,
                        onSave,
                        onCancel,
                      }: {
  draft: LocationDraft;
  error: string | null;
  isSaving: boolean;
  onChange: (draft: LocationDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const setField = <K extends keyof LocationDraft>(key: K, value: LocationDraft[K]) => onChange({ ...draft, [key]: value });
  return (
      <FormLayout error={error} isSaving={isSaving} onSave={onSave} onCancel={onCancel}>
        <SettingsFormGroup title="Location">
          <div className="grid gap-4 md:grid-cols-3">
            <Input label="Location name" value={draft.displayName} onChange={(event) => setField('displayName', event.target.value)} />
            <Input label="Court label" hint="Court, field, or meeting point." value={draft.courtLabel} onChange={(event) => setField('courtLabel', event.target.value)} />
            <Input label="ZIP code" value={draft.zipcode} onChange={(event) => setField('zipcode', event.target.value)} />
          </div>
        </SettingsFormGroup>
        <details className="mt-4 rounded-lg border border-border bg-surface px-4 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-text">Address and ordering</summary>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Input label="Address" value={draft.addressLine1} onChange={(event) => setField('addressLine1', event.target.value)} />
            <Input label="City" value={draft.city} onChange={(event) => setField('city', event.target.value)} />
            <Input label="State" value={draft.stateCode} onChange={(event) => setField('stateCode', event.target.value)} />
            <Input label="Sort" type="number" min={0} value={draft.sortOrder} onChange={(event) => setField('sortOrder', event.target.value)} />
          </div>
        </details>
        <ActiveToggle checked={draft.active} onChange={(checked) => setField('active', checked)} />
      </FormLayout>
  );
}

function SeasonForm({
                      draft,
                      error,
                      isSaving,
                      onChange,
                      onSave,
                      onCancel,
                    }: {
  draft: SeasonDraft;
  error: string | null;
  isSaving: boolean;
  onChange: (draft: SeasonDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const setField = <K extends keyof SeasonDraft>(key: K, value: SeasonDraft[K]) => onChange({ ...draft, [key]: value });
  return (
      <FormLayout error={error} isSaving={isSaving} onSave={onSave} onCancel={onCancel}>
        <SettingsFormGroup title="Season window">
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Season name" hint="Reusable label, such as Summer or Fall." value={draft.displayName} onChange={(event) => setField('displayName', event.target.value)} />
            <Input label="Sort" type="number" min={0} value={draft.sortOrder} onChange={(event) => setField('sortOrder', event.target.value)} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-4">
            <Input label="Start month" type="number" min={1} max={12} value={draft.startsMonth} onChange={(event) => setField('startsMonth', event.target.value)} />
            <Input label="Start day" type="number" min={1} max={31} value={draft.startsDay} onChange={(event) => setField('startsDay', event.target.value)} />
            <Input label="End month" type="number" min={1} max={12} value={draft.endsMonth} onChange={(event) => setField('endsMonth', event.target.value)} />
            <Input label="End day" type="number" min={1} max={31} value={draft.endsDay} onChange={(event) => setField('endsDay', event.target.value)} />
          </div>
        </SettingsFormGroup>
        <ActiveToggle checked={draft.active} onChange={(checked) => setField('active', checked)} />
      </FormLayout>
  );
}

function CategoryForm({
                        draft,
                        error,
                        isSaving,
                        onChange,
                        onSave,
                        onCancel,
                      }: {
  draft: CategoryDraft;
  error: string | null;
  isSaving: boolean;
  onChange: (draft: CategoryDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const setField = <K extends keyof CategoryDraft>(key: K, value: CategoryDraft[K]) => onChange({ ...draft, [key]: value });
  return (
      <FormLayout error={error} isSaving={isSaving} onSave={onSave} onCancel={onCancel}>
        <SettingsFormGroup title="Class identity">
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Class category" value={draft.displayName} onChange={(event) => setField('displayName', event.target.value)} />
            <SelectField
                label="Type"
                value={draft.programType}
                options={[
                  { value: 'GROUP_CLINIC', label: 'Group clinic' },
                  { value: 'CAMP', label: 'Camp' },
                  { value: 'PRIVATE_LESSON', label: 'Private lesson' },
                  { value: 'TEAM_TRAINING', label: 'Team training' },
                  { value: 'OPEN_PLAY', label: 'Open play' },
                ]}
                onChange={(value) => setField('programType', value)}
            />
            <Input label="Age" hint="Age band shown to families." value={draft.ageLabel} onChange={(event) => setField('ageLabel', event.target.value)} />
            <Input label="Level" hint="Ball color, pathway, or skill level." value={draft.levelLabel} onChange={(event) => setField('levelLabel', event.target.value)} />
            <SelectField
                label="Sport"
                value={draft.sportCode}
                options={[
                  { value: 'TENNIS', label: 'Tennis' },
                  { value: 'PICKLEBALL', label: 'Pickleball' },
                  { value: 'BOTH', label: 'Both' },
                ]}
                onChange={(value) => setField('sportCode', value)}
            />
            <Input label="Capacity" type="number" min={1} value={draft.capacity} onChange={(event) => setField('capacity', event.target.value)} />
          </div>
        </SettingsFormGroup>
        <SettingsFormGroup title="Pricing">
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Fee" type="number" min={0} value={draft.priceAmount} onChange={(event) => setField('priceAmount', event.target.value)} />
            <Input label="Fee text" hint="Optional public display label." value={draft.priceLabel} onChange={(event) => setField('priceLabel', event.target.value)} />
          </div>
        </SettingsFormGroup>
        <details className="mt-4 rounded-lg border border-border bg-surface px-4 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-text">Schedule and ordering</summary>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Input label="Time" hint="Class time shown to families." value={draft.scheduleLabel} onChange={(event) => setField('scheduleLabel', event.target.value)} />
            <Input label="Days" hint="Day rule for 2-day or 4-day options." value={draft.daysRule} onChange={(event) => setField('daysRule', event.target.value)} />
            <Input label="Sort" type="number" min={0} value={draft.sortOrder} onChange={(event) => setField('sortOrder', event.target.value)} />
          </div>
        </details>
        <ActiveToggle checked={draft.active} onChange={(checked) => setField('active', checked)} />
      </FormLayout>
  );
}

function FormLayout({
                      error,
                      isSaving,
                      onSave,
                      onCancel,
                      children,
                    }: {
  error: string | null;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
      <form
          onSubmit={(event) => {
            event.preventDefault();
            onSave();
          }}
      >
        {children}
        {error ? (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {error}
            </p>
        ) : null}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save
          </Button>
        </div>
      </form>
  );
}

function SettingsFormGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
      <div className="rounded-lg border border-border bg-surface-elevated/20 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">{title}</p>
        {children}
      </div>
  );
}

function ActiveToggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
      <label
          className={cn(
              'mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-surface px-4 py-3 text-sm font-semibold text-text transition-colors',
              checked ? 'border-emerald-200 bg-emerald-50/40' : 'border-border'
          )}
      >
      <span className="flex items-center gap-2">
        <span
            className={cn(
                'h-2 w-2 rounded-full',
                checked ? 'bg-emerald-500' : 'bg-slate-400'
            )}
        />
        Active
      </span>
        <input
            type="checkbox"
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
        />
      </label>
  );
}

function SelectField({
                       label,
                       value,
                       options,
                       onChange,
                     }: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
      <label className="space-y-1.5">
        <span className="block text-sm font-medium text-text">{label}</span>
        <select
            className="h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 hover:border-border-hover"
            value={value}
            onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
          ))}
        </select>
      </label>
  );
}

// ============================================
// Drafts / payload helpers (unchanged)
// ============================================
function emptyLocationDraft(): LocationDraft {
  return {
    displayName: '',
    courtLabel: '',
    addressLine1: '',
    city: '',
    stateCode: '',
    zipcode: '',
    sortOrder: '0',
    active: true,
  };
}

function emptySeasonDraft(): SeasonDraft {
  return {
    displayName: '',
    startsMonth: '',
    startsDay: '',
    endsMonth: '',
    endsDay: '',
    sortOrder: '0',
    active: true,
  };
}

function emptyCategoryDraft(): CategoryDraft {
  return {
    displayName: '',
    ageLabel: '',
    levelLabel: '',
    programType: 'GROUP_CLINIC',
    sportCode: 'TENNIS',
    priceAmount: '',
    priceLabel: '',
    scheduleLabel: '',
    daysRule: '',
    capacity: '',
    sortOrder: '0',
    active: true,
  };
}

function toLocationDraft(item?: TenantStudioLocation | null): LocationDraft {
  return {
    displayName: item?.displayName ?? '',
    courtLabel: item?.courtLabel ?? '',
    addressLine1: item?.addressLine1 ?? '',
    city: item?.city ?? '',
    stateCode: item?.stateCode ?? '',
    zipcode: item?.zipcode ?? '',
    sortOrder: item?.sortOrder != null ? String(item.sortOrder) : '0',
    active: item?.active ?? true,
  };
}

function toSeasonDraft(item?: TenantStudioSeason | null): SeasonDraft {
  return {
    displayName: item?.displayName ?? '',
    startsMonth: item?.startsMonth != null ? String(item.startsMonth) : '',
    startsDay: item?.startsDay != null ? String(item.startsDay) : '',
    endsMonth: item?.endsMonth != null ? String(item.endsMonth) : '',
    endsDay: item?.endsDay != null ? String(item.endsDay) : '',
    sortOrder: item?.sortOrder != null ? String(item.sortOrder) : '0',
    active: item?.active ?? true,
  };
}

function toCategoryDraft(item?: TenantStudioCategory | null): CategoryDraft {
  return {
    displayName: item?.displayName ?? '',
    ageLabel: item?.ageLabel ?? '',
    levelLabel: item?.levelLabel ?? '',
    programType: item?.programType ?? 'GROUP_CLINIC',
    sportCode: item?.sportCode ?? 'TENNIS',
    priceAmount: item?.priceAmount != null ? String(item.priceAmount) : '',
    priceLabel: item?.priceLabel ?? '',
    scheduleLabel: item?.scheduleLabel ?? '',
    daysRule: item?.daysRule ?? '',
    capacity: item?.capacity != null ? String(item.capacity) : '',
    sortOrder: item?.sortOrder != null ? String(item.sortOrder) : '0',
    active: item?.active ?? true,
  };
}

function buildLocationPayload(draft: LocationDraft): TenantStudioLocationPayload {
  return {
    displayName: draft.displayName.trim(),
    courtLabel: trimOrNull(draft.courtLabel),
    addressLine1: trimOrNull(draft.addressLine1),
    city: trimOrNull(draft.city),
    stateCode: trimOrNull(draft.stateCode),
    zipcode: trimOrNull(draft.zipcode),
    sortOrder: toNumberOrNull(draft.sortOrder),
    active: draft.active,
  };
}

function buildSeasonPayload(draft: SeasonDraft): TenantStudioSeasonPayload {
  return {
    displayName: draft.displayName.trim(),
    startsMonth: toNumberOrNull(draft.startsMonth),
    startsDay: toNumberOrNull(draft.startsDay),
    endsMonth: toNumberOrNull(draft.endsMonth),
    endsDay: toNumberOrNull(draft.endsDay),
    sortOrder: toNumberOrNull(draft.sortOrder),
    active: draft.active,
  };
}

function buildCategoryPayload(draft: CategoryDraft): TenantStudioCategoryPayload {
  return {
    displayName: draft.displayName.trim(),
    ageLabel: trimOrNull(draft.ageLabel),
    levelLabel: trimOrNull(draft.levelLabel),
    programType: draft.programType || 'GROUP_CLINIC',
    sportCode: draft.sportCode || 'TENNIS',
    priceAmount: toNumberOrNull(draft.priceAmount),
    priceLabel: trimOrNull(draft.priceLabel),
    scheduleLabel: trimOrNull(draft.scheduleLabel),
    daysRule: trimOrNull(draft.daysRule),
    capacity: toNumberOrNull(draft.capacity),
    sortOrder: toNumberOrNull(draft.sortOrder),
    active: draft.active,
  };
}

function formatSeasonWindow(season: TenantStudioSeason): string {
  const start = season.startsMonth && season.startsDay ? `${season.startsMonth}/${season.startsDay}` : '';
  const end = season.endsMonth && season.endsDay ? `${season.endsMonth}/${season.endsDay}` : '';
  if (start && end) {
    return `${start} - ${end}`;
  }
  return 'Annual preset';
}

function formatMoney(value?: number | null): string {
  if (value == null) {
    return '';
  }
  return `$${Number(value).toLocaleString('en-US')}`;
}

function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
